import Documents from "components/Documents/drawer/DocumentsDrawer";
import ErrorState from "components/Error/ErrorState";
import LoadingIcon from "components/Loading/LoadingIcon";
import TaskModal from "pages/tasks/components/modals/TaskModal";
import OverviewStatusHeader from "components/StatusHeader/OverviewStatusHeader";
import { getStatusTitles } from "components/StatusHeader/StatusHeaderUtils";
import { SECTION } from "constants/SettingsConstants";
import { BackgroundJobsDrawer } from "components/BackgroundJobsDrawer/BackgroundJobsDrawer";
import ClientInformationDrawer from "pages/client/ClientInformationDrawer/ClientInformationDrawer";
import CreateLienModal from "pages/liens/CreateLienModal";
import { useLiensData } from "pages/liens/hooks/useLiensData";
import TriggerOCRModal from "pages/liens/trigger-ocr/TriggerOCRModal";
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { clearCurrentTask } from "reducers/files";
import { setShowCreateLienModal } from "reducers/liens";
import {
  setIsLoading,
  setShowTaskModal,
  setValidationState,
} from "reducers/main";
import { useGetOrganizationSettingsQuery } from "services/misc-api";
import { useDispatch, useSelector } from "utils/redux";

import { useLienFileData } from "./hooks/useLienFileData";
import type { Lien } from "./LienTypes";

type LienOverviewProps = {
  className?: string;
};

type LienGroupProps = {
  title: string;
  liens?: Lien[];
  lienFileId: string;
  isPlanType?: boolean;
  quickbooksIntegrationEnabled?: boolean;
};

const NOTICE_BADGES = [
  {
    field: "cpn_received",
    label: "CPN",
    title: "CPN received",
    tone: "cpn",
  },
  {
    field: "cpl_received",
    label: "CPL",
    title: "CPL received",
    tone: "cpl",
  },
  {
    field: "demand_received",
    label: "DEMAND",
    title: "Demand received",
    tone: "demand",
  },
] as const;

const getLienCaseTitle = (
  lien: Lien,
  index: number,
  isPlanType: boolean,
): string =>
  isPlanType
    ? `Plan ${index + 1} - ${lien.case_number}`
    : `Case: ${lien.case_number}`;

const LienNoticeBadges = ({ lien }: { lien: Lien }) => {
  const receivedBadges = NOTICE_BADGES.filter(({ field }) => lien[field]);

  if (receivedBadges.length === 0) {
    return null;
  }

  return (
    <span
      className="lien-overview-notice-badges"
      aria-label={`Received notices: ${receivedBadges
        .map(({ label }) => label)
        .join(", ")}`}
    >
      {receivedBadges.map(({ label, title, tone }) => (
        <span
          key={label}
          className={`lien-overview-notice-badges__badge lien-overview-notice-badges__badge--${tone}`}
          title={title}
        >
          {label}
        </span>
      ))}
    </span>
  );
};

const LienGroup = ({
  title,
  liens,
  lienFileId,
  isPlanType = false,
  quickbooksIntegrationEnabled = true,
}: LienGroupProps) => {
  return (
    <div className="lien-group">
      <div className="header-primary">{title}</div>
      {liens?.length ? (
        liens.map((lien, index) => (
          <OverviewStatusHeader
            key={lien.id}
            title={getLienCaseTitle(lien, index, isPlanType)}
            headerSuffix={<LienNoticeBadges lien={lien} />}
            workspaceUrl={`/liens/${lienFileId}/lien/${lien.id}`}
            items={Object.entries(
              getStatusTitles(
                {
                  type: SECTION.LIENS,
                  validation: lien.validations,
                  lienFileId: lienFileId,
                  id: lien.id.toString(),
                  fileId: lien.file.id.toString(),
                },
                quickbooksIntegrationEnabled,
              ),
            ).map(([title, { status, url }]) => ({
              title,
              status,
              url,
            }))}
          />
        ))
      ) : (
        <p className="text-center">No {title} exist.</p>
      )}
    </div>
  );
};

const LienOverview = ({ className }: LienOverviewProps) => {
  const dispatch = useDispatch();
  const { data: organizationSettings } = useGetOrganizationSettingsQuery();
  const { lienFileId } = useParams<{ lienFileId: string }>();
  const params = new URLSearchParams(location.search);
  const fileId = params.get("file_id");
  const [showOCRModal, setShowOCRModal] = useState(false);

  const showCreateLienModal = useSelector(
    (state) => state.liens.showCreateLienModal,
  );

  const showTaskModal = useSelector((state) => state.main.showTaskModal);

  const {
    liens,
    isLoading: liensLoading,
    error: liensError,
  } = useLiensData({ fileId });

  const { lienFileData } = useLienFileData();

  // clear validation state on mount and set lien validations
  useEffect(() => {
    dispatch(setValidationState({ errors: [], warnings: [] }));
  }, [dispatch, liens]);

  useEffect(() => {
    dispatch(setIsLoading(liensLoading));
  }, [dispatch, liensLoading]);

  const handleClose = useCallback(() => {
    dispatch(clearCurrentTask());
    dispatch(setShowTaskModal(false));
  }, [dispatch]);

  const partABLiens = liens?.results?.filter((lien) => lien.part_type === "AB");
  const partCLiens = liens?.results?.filter((lien) => lien.part_type === "C");
  const partDLiens = liens?.results?.filter((lien) => lien.part_type === "D");

  if (!lienFileId) {
    return <ErrorState />;
  }

  if (liensLoading) {
    return <LoadingIcon />;
  }

  if (liensError) {
    return <ErrorState />;
  }

  return (
    <>
      <CreateLienModal
        isOpen={showCreateLienModal}
        onClose={() => dispatch(setShowCreateLienModal(false))}
        fileId={fileId || ""}
        lienFileId={lienFileId}
      />
      {showTaskModal && (
        <TaskModal
          isOpen={showTaskModal}
          onClose={handleClose}
          fileId={fileId}
          serviceId={Number(liens?.results?.[0]?.id) || 0}
          isLien={true}
        />
      )}
      {showOCRModal && fileId && (
        <TriggerOCRModal
          isOpen={showOCRModal}
          onClose={() => setShowOCRModal(false)}
        />
      )}
      <div className={`overview-page ${className || ""}`}>
        <div className="timeline-overview">
          <div className="grid-layout status-overview">
            <LienGroup
              title="Part A/B Liens"
              liens={partABLiens}
              lienFileId={lienFileId}
              quickbooksIntegrationEnabled={
                organizationSettings?.quickbooks_integration_enabled ?? true
              }
            />
            <LienGroup
              title="Part C Liens"
              liens={partCLiens}
              lienFileId={lienFileId}
              isPlanType={true}
              quickbooksIntegrationEnabled={
                organizationSettings?.quickbooks_integration_enabled ?? true
              }
            />
            <LienGroup
              title="Part D Liens"
              liens={partDLiens}
              lienFileId={lienFileId}
              isPlanType={true}
              quickbooksIntegrationEnabled={
                organizationSettings?.quickbooks_integration_enabled ?? true
              }
            />
          </div>
        </div>
        <BackgroundJobsDrawer filters={{ lien_file_id: lienFileId }} />
        <Documents
          fileId={Number(fileId)}
          serviceId={Number(lienFileId)}
          serviceType="lien"
        />
        <ClientInformationDrawer service={lienFileData} />
      </div>
    </>
  );
};

export default LienOverview;
